import type { UseQueueReturn } from '@proj-airi/stream-kit'

import type { EmotionPayload } from '../constants/emotions'

import { sleep } from '@moeru/std'
import { createQueue } from '@proj-airi/stream-kit'

import { Emotion, EMOTION_VALUES } from '../constants/emotions'

export function useEmotionsMessageQueue(emotionsQueue: UseQueueReturn<EmotionPayload>) {
  const normalizeEmotionName = (value: string): Emotion | null => {
    const normalized = value.trim().toLowerCase()
    if (EMOTION_VALUES.includes(normalized as Emotion))
      return normalized as Emotion
    return null
  }

  const normalizeIntensity = (value: unknown): number => {
    if (typeof value !== 'number' || Number.isNaN(value))
      return 1
    return Math.min(1, Math.max(0, value))
  }

  // Extract the first balanced JSON object from a string, ignoring trailing garbage
  function extractBalancedJson(str: string): string | null {
    if (str[0] !== '{')
      return null
    let depth = 0
    for (let i = 0; i < str.length; i++) {
      if (str[i] === '{')
        depth++
      else if (str[i] === '}')
        depth--
      if (depth === 0)
        return str.slice(0, i + 1)
    }
    return null
  }

  function parseActEmotion(content: string): { ok: boolean, emotion: EmotionPayload | null, expression: string | null } {
    // Capture everything between ACT: and |> (lazy match)
    const match = /<\|ACT\s*(?::\s*)?([\s\S]*?)\|>/i.exec(content)
    if (!match)
      return { ok: false, emotion: null, expression: null }

    let payloadText = match[1].trim()

    // Wrap in braces if the LLM omitted them
    // e.g. <|ACT:"emotion":{"name":"happy"}|> → {"emotion":{"name":"happy"}}
    if (!payloadText.startsWith('{'))
      payloadText = `{${payloadText}}`

    // Extract only the balanced JSON object, stripping trailing garbage
    // e.g. {"emotion":{"name":"surprised"}}<|DELAY:1 → {"emotion":{"name":"surprised"}}
    const balanced = extractBalancedJson(payloadText)
    if (balanced)
      payloadText = balanced

    try {
      const payload = JSON.parse(payloadText) as { emotion?: unknown, expression?: unknown }

      // Extract expression field
      const expression = typeof payload?.expression === 'string' ? payload.expression : null

      // Parse emotion
      const emotion = payload?.emotion
      if (typeof emotion === 'string') {
        const normalized = normalizeEmotionName(emotion)
        if (normalized)
          return { ok: true, emotion: { name: normalized, intensity: 1, ...(expression ? { expression } : {}) }, expression }
      }
      else if (emotion && typeof emotion === 'object' && !Array.isArray(emotion)) {
        if ('name' in emotion && typeof (emotion as { name?: unknown }).name === 'string') {
          const normalized = normalizeEmotionName((emotion as { name: string }).name)
          if (normalized) {
            const intensity = normalizeIntensity((emotion as { intensity?: unknown }).intensity)
            return { ok: true, emotion: { name: normalized, intensity, ...(expression ? { expression } : {}) }, expression }
          }
        }
      }

      // Expression only, no emotion — use neutral as default
      if (expression)
        return { ok: true, emotion: { name: Emotion.Neutral as Emotion, intensity: 1, expression }, expression }
    }
    catch (e) {
      console.warn(`[parseActEmotion] Failed to parse ACT payload JSON: "${payloadText}"`, e)
    }

    return { ok: false, emotion: null, expression: null }
  }

  return createQueue<string>({
    handlers: [
      async (ctx) => {
        const actParsed = parseActEmotion(ctx.data)
        if (actParsed.ok && actParsed.emotion) {
          ctx.emit('emotion', actParsed.emotion)
          emotionsQueue.enqueue(actParsed.emotion)
        }
      },
    ],
  })
}

export function useDelayMessageQueue() {
  function splitDelays(content: string) {
    if (!(/<\|DELAY:\d+\|>/i.test(content))) {
      return {
        ok: false,
        delay: 0,
      }
    }

    const delayExecArray = /<\|DELAY:(\d+)\|>/i.exec(content)

    const delay = delayExecArray?.[1]
    if (!delay) {
      return {
        ok: false,
        delay: 0,
      }
    }

    const delaySeconds = Number.parseFloat(delay)

    if (delaySeconds <= 0 || Number.isNaN(delaySeconds)) {
      return {
        ok: true,
        delay: 0,
      }
    }

    return {
      ok: true,
      delay: delaySeconds,
    }
  }

  return createQueue<string>({
    handlers: [
      async (ctx) => {
        const { ok, delay } = splitDelays(ctx.data)
        if (ok) {
          ctx.emit('delay', delay)
          await sleep(delay * 1000)
        }
      },
    ],
  })
}
